import axiosClient from '@/services/shared/api/axios-client';
import config from '@/config/config';
import { 
  Exercise, 
  CreateExerciseInput, 
  UpdateExerciseInput
} from './exercises.interfaces';

// Real API implementations
export const apiCreateExercise = async (input: CreateExerciseInput): Promise<Exercise> => {
  console.log('🌐 ===== INÍCIO apiCreateExercise =====');
  console.log('🌐 input.moduleId:', input.moduleId);
  console.log('🌐 input completo:', JSON.stringify(input, null, 2));
  
  try {
    // Extract moduleId
    const moduleId = input.moduleId;
    console.log('🌐 moduleId extraído:', moduleId);

    // Format data for API and determine title based on exercise type
    let title = 'Exercise';
    if ('question_text' in input.data) {
      title = (input.data as any).question_text;
    } else if ('question' in input.data) {
      title = (input.data as any).question;
    } else if ('text' in input.data) {
      title = (input.data as any).text.substring(0, 30); // Use first 30 chars of text as title
    }

    const exerciseData = {
      title: title,
      description: 'Exercise Description',
      type: input.type,
      data: JSON.stringify(input.data),
      order: input.order || 1,
      moduleId: moduleId
    };

    console.log('🌐 exerciseData.moduleId:', exerciseData.moduleId);
    console.log('🌐 exerciseData completo:', JSON.stringify(exerciseData, null, 2));
    console.log('🌐 URL da API:', `${config.apiBaseUrl}/modules/${moduleId}/exercises`);

    // Call the API with the new URL structure
    const response = await axiosClient.post<Exercise>(
      `${config.apiBaseUrl}/modules/${moduleId}/exercises`,
      exerciseData
    );

    console.log('🌐 Resposta da API:', JSON.stringify(response.data, null, 2));
    console.log('🌐 ===== FIM apiCreateExercise =====');

    // Parse the response data
    const responseData = response.data;

    // If data is a string, parse it
    if (typeof responseData.data === 'string') {
      try {
        responseData.data = JSON.parse(responseData.data);
      } catch (e) {
        console.error('Error parsing exercise data:', e);
      }
    }

    return responseData;
  } catch (error) {
    console.error('Error creating exercise:', error);
    throw error;
  }
};

export const apiUpdateExercise = async (exerciseId: string, input: UpdateExerciseInput): Promise<Exercise> => {
  try {
    console.log('🌐 apiUpdateExercise recebeu:', JSON.stringify({ exerciseId, input }, null, 2));
    
    // Get moduleId from input
    const moduleId = input.moduleId;
    console.log('🌐 moduleId extraído:', moduleId);
    console.log('🌐 exerciseId recebido:', exerciseId);

    if (!moduleId) {
      throw new Error('Module ID is required for updating exercises');
    }

    // Format data for API and determine title based on exercise type
    let title = 'Updated Exercise';
    let dataWithoutCorrectness: any = undefined;
    let answerData: string | undefined = undefined;

    if (input.data) {
      if ('question_text' in input.data) {
        title = (input.data as any).question_text;
      } else if ('question' in input.data) {
        title = (input.data as any).question;
      } else if ('text' in input.data) {
        title = (input.data as any).text.substring(0, 30); // Use first 30 chars of text as title
      }

      // For multiple-choice exercises, extract correct answer IDs and remove isCorrect flags
      if (input.type === 'multiple_choice' && input.data && typeof input.data === 'object' && 'options' in input.data) {
        const mcData = input.data as any;
        const options = mcData.options || [];
        
        // Extract correct answer IDs (convert string IDs to integers)
        const correctAnswerIds = options
          .filter((opt: any) => opt.isCorrect)
          .map((opt: any) => {
            const id = typeof opt.id === 'string' ? parseInt(opt.id) : opt.id;
            return typeof id === 'string' ? parseInt(id) : id;
          })
          .filter((id: any) => !isNaN(id));

        // Build data without isCorrect flags
        dataWithoutCorrectness = {
          question: mcData.question,
          options: options.map((opt: any) => ({
            id: typeof opt.id === 'string' ? parseInt(opt.id) : opt.id,
            text: opt.text,
            order: opt.order
          })),
          multipleCorrectAnswers: mcData.multipleCorrectAnswers || false
        };

        // Set answerData as JSON array string
        answerData = JSON.stringify(correctAnswerIds);
      } else {
        // For non-multiple-choice exercises, use data as-is
        dataWithoutCorrectness = input.data;
      }
    }

    const exerciseData: any = {
      id: exerciseId,
      title: title,
      description: 'Updated Exercise Description',
      type: input.type,
      data: dataWithoutCorrectness ? JSON.stringify(dataWithoutCorrectness) : undefined,
      order: input.order,
      moduleId: moduleId,
      isActive: input.isActive !== false,
    };

    // Add answerData if it exists (backend may accept it even if not in DTO)
    if (answerData) {
      exerciseData.answerData = answerData;
    }

    console.log('🌐 Dados sendo enviados para API UPDATE:', JSON.stringify(exerciseData, null, 2));
    console.log('🌐 URL da API:', `${config.apiBaseUrl}/modules/${moduleId}/exercises/${exerciseId}`);

    // Call the API with the new URL structure
    const response = await axiosClient.put<Exercise>(
      `${config.apiBaseUrl}/modules/${moduleId}/exercises/${exerciseId}`,
      exerciseData
    );

    console.log('🌐 Resposta da API UPDATE:', JSON.stringify(response.data, null, 2));

    // Parse the response data
    const responseData = response.data;

    // If data is a string, parse it
    if (typeof responseData.data === 'string') {
      try {
        responseData.data = JSON.parse(responseData.data);
      } catch (e) {
        console.error('Error parsing exercise data:', e);
      }
    }

    return responseData;
  } catch (error) {
    console.error('Error updating exercise:', error);
    throw error;
  }
};

export const apiDeleteExercise = async (exerciseId: string, moduleId: string): Promise<void> => {
  try {
    // Call the API with the new URL structure
    await axiosClient.delete(
      `${config.apiBaseUrl}/modules/${moduleId}/exercises/${exerciseId}`
    );
  } catch (error) {
    console.error('Error deleting exercise:', error);
    throw error;
  }
};

export const apiListExercisesByModule = async (moduleId: string): Promise<Exercise[]> => {
  const url = `${config.apiBaseUrl}/modules/${moduleId}/exercises`;
  console.log('Fetching exercises from URL:', url, 'ModuleId:', moduleId);

  const response = await axiosClient.get<Exercise[]>(url);

  // Validate the response data structure
  if (Array.isArray(response.data)) {
    console.log('Number of exercises:', response.data.length);
  }

  // Parse the JSON string in the data field
  if (Array.isArray(response.data)) {
    const parsedExercises = response.data.map(exercise => {
      if (typeof exercise.data === 'string') {
        try {
          exercise.data = JSON.parse(exercise.data);
        } catch (e) {
          console.error('Error parsing exercise data:', e, 'Raw data:', exercise.data);
        }
      }
      return exercise;
    });

    return parsedExercises;
  }
  return response.data;
};

// Método para atualizar a ordem dos exercícios em uma única chamada
export const apiUpdateExercisesOrder = async (moduleId: string, exerciseIds: (string | number)[]): Promise<void> => {
  try {
    const orderData = exerciseIds;
    
    await axiosClient.put(
      `${config.apiBaseUrl}/modules/${moduleId}/exercises/reorder`,
      orderData
    );
    
    console.log('Ordem dos exercícios atualizada com sucesso');
  } catch (error) {
    console.error('Erro ao atualizar ordem dos exercícios:', error);
    throw error;
  }
};

// Level Test Group Exercises
export const apiListExercisesByLevelTestGroup = async (levelTestGroupId: string): Promise<Exercise[]> => {
  const url = `${config.apiBaseUrl}/admin/level-test-groups/${levelTestGroupId}/exercises`;
  const response = await axiosClient.get<Exercise[]>(url);
  
  // Map exercise types from backend format to frontend format
  const backendToFrontendMapping: Record<string, string> = {
    'MultipleChoice': 'multiple_choice',
    'Essay': 'essay',
    'TrueFalse': 'true_false',
    'FillInTheBlank': 'fill_in_the_blank',
    'Matching': 'matching'
  };
  
  return response.data.map(exercise => {
    // Parse the JSON string in the data field
    if (typeof exercise.data === 'string') {
      try {
        exercise.data = JSON.parse(exercise.data);
      } catch (e) {
        console.error('Error parsing exercise data:', e, 'Raw data:', exercise.data);
      }
    }
    
    return {
      ...exercise,
      type: backendToFrontendMapping[exercise.type] || exercise.type
    };
  });
};

export const apiCreateExerciseForLevelTestGroup = async (input: any): Promise<Exercise> => {
  const { levelTestGroupId, type, data, ...rest } = input;
  
  // Map exercise types from frontend format to backend format
  const typeMapping: Record<string, string> = {
    'multiple_choice': 'MultipleChoice',
    'essay': 'Essay',
    'true_false': 'TrueFalse',
    'fill_in_the_blank': 'FillInTheBlank',
    'matching': 'Matching'
  };
  
  const backendType = typeMapping[type] || type;
  
  // Generate title from exercise data
  let title = 'Exercise';
  if (data && typeof data === 'object') {
    if (data.question) {
      title = data.question.substring(0, 200); // Respect max length
    } else if (data.text) {
      title = data.text.substring(0, 200);
    } else if (data.prompt) {
      title = data.prompt.substring(0, 200);
    }
  }
  
  // Map to expected backend structure
  const exerciseData = {
    Title: title,
    Description: title, // Use title as description for now
    Type: backendType,
    Data: JSON.stringify(data) // Convert data object to JSON string
  };
  
  const response = await axiosClient.post<Exercise>(
    `${config.apiBaseUrl}/admin/level-test-groups/${levelTestGroupId}/exercises`,
    exerciseData
  );
  return response.data;
};

export const apiUpdateExerciseForLevelTestGroup = async (exerciseId: string, input: any): Promise<Exercise> => {
  const { levelTestGroupId, type, data, ...rest } = input;
  
  // Map exercise types from frontend format to backend format
  const typeMapping: Record<string, string> = {
    'multiple_choice': 'MultipleChoice',
    'essay': 'Essay',
    'true_false': 'TrueFalse',
    'fill_in_the_blank': 'FillInTheBlank',
    'matching': 'Matching'
  };
  
  const backendType = typeMapping[type] || type;
  
  // Generate title from exercise data
  let title = 'Exercise';
  if (data && typeof data === 'object') {
    if (data.question) {
      title = data.question.substring(0, 200); // Respect max length
    } else if (data.text) {
      title = data.text.substring(0, 200);
    } else if (data.prompt) {
      title = data.prompt.substring(0, 200);
    }
  }
  
  // Send as JSON object - backend will be updated to accept it
  const exerciseData = {
    title: title,
    description: title, // Use title as description for now
    type: backendType,
    data: JSON.stringify(data) // Convert data object to JSON string
  };
  
  const response = await axiosClient.put<Exercise>(
    `${config.apiBaseUrl}/admin/level-test-groups/${levelTestGroupId}/exercises/${exerciseId}`,
    exerciseData
  );
  return response.data;
};

export const apiDeleteExerciseForLevelTestGroup = async (exerciseId: string, levelTestGroupId: string): Promise<void> => {
  await axiosClient.delete(
    `${config.apiBaseUrl}/admin/level-test-groups/${levelTestGroupId}/exercises/${exerciseId}`
  );
};

export const apiUpdateLevelTestGroupExercisesOrder = async (levelTestGroupId: string, exerciseIds: (string | number)[]): Promise<void> => {
  await axiosClient.put(
    `${config.apiBaseUrl}/admin/level-test-groups/${levelTestGroupId}/exercises/reorder`,
    exerciseIds
  );
};

// Admin-specific API functions with AnswerData support
export interface AdminLevelTestGroupExerciseDetail {
  id: string;
  title: string;
  description: string;
  type: string;
  order: number;
  data: string; // JSON string
  answerData: string; // JSON string with correct answer IDs
}

export const apiGetAdminExerciseDetail = async (
  levelTestGroupId: string,
  exerciseId: string
): Promise<AdminLevelTestGroupExerciseDetail> => {
  const response = await axiosClient.get<AdminLevelTestGroupExerciseDetail>(
    `${config.apiBaseUrl}/admin/level-test-groups/${levelTestGroupId}/exercises/${exerciseId}/details`
  );
  return response.data;
};

export const apiUpdateExerciseForLevelTestGroupAdmin = async (
  levelTestGroupId: string,
  exerciseId: string,
  input: {
    title: string;
    description?: string;
    type: string;
    data: string; // JSON string without isCorrect
    answerData: string; // JSON string with correct answer IDs
  }
): Promise<AdminLevelTestGroupExerciseDetail> => {
  const response = await axiosClient.put<AdminLevelTestGroupExerciseDetail>(
    `${config.apiBaseUrl}/admin/level-test-groups/${levelTestGroupId}/exercises/${exerciseId}/admin`,
    input
  );
  return response.data;
}; 